import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ChevronLeft, BookOpen, Film, ExternalLink, Loader2, Sparkles } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { ListenButton } from "../components/ListenButton";
import { useTranslate } from "../context/TranslateContext";
import { getArticle, articlePlainText, HISTORY_INTRO, SERIES, WIKI, cmsToArticle } from "../mock/articles";
import { getIndustry, getEditorial, getContent } from "../lib/api";
import { LandmarkSerials } from "../components/LandmarkSerials";
import { Discussion } from "../components/Discussion";

const INDUSTRY_HERO = "https://images.unsplash.com/photo-1478720568477-152d9b164e26?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Article() {
  const { slug } = useParams();
  const { t } = useTranslate();
  const isIndustry = slug?.startsWith("industry-");
  const isEditorial = slug?.startsWith("editorial-");
  const isCms = slug?.startsWith("cms-");
  const isDynamic = isIndustry || isEditorial || isCms;
  const [dyn, setDyn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    if (!isDynamic) { setDyn(null); return; }
    let cancelled = false;
    setLoading(true); setError(false); setDyn(null);
    let fetcher;
    if (isIndustry) fetcher = getIndustry(slug.replace("industry-", "")).then((d) => ({ ...d, hero: d.hero || INDUSTRY_HERO }));
    else if (isEditorial) fetcher = getEditorial(slug.replace("editorial-", "")).then((d) => ({ ...d, hero: d.hero || INDUSTRY_HERO }));
    else fetcher = getContent(slug.replace("cms-", "")).then((doc) => {
      if (doc.status !== "approved") throw new Error("not published");
      return cmsToArticle(doc);
    });
    fetcher
      .then((d) => { if (!cancelled) { setDyn(d); } })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, isIndustry, isEditorial, isCms, isDynamic, retryKey]);

  const a = isDynamic ? dyn : getArticle(slug);

  if (isDynamic && loading) {
    return (
      <div className="min-h-screen bg-paper" data-testid="article-loading">
        <InnerNav />
        <div className="mx-auto flex max-w-2xl flex-col items-center px-6 py-40 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gold" />
          <div className="mt-6 flex items-center gap-2 font-display text-2xl text-cream">
            <Sparkles className="h-5 w-5 text-gold" /> {t("Composing this article with AI…")}
          </div>
          <p className="mt-3 font-body text-sm text-ash">{t("Researching the industry's history, films and music. This takes a few seconds the first time.")}</p>
        </div>
      </div>
    );
  }

  if (!a) {
    const cmsErr = isCms;
    return (
      <div className="min-h-screen bg-paper">
        <InnerNav />
        <div className="mx-auto max-w-2xl px-6 py-32 text-center">
          <h1 className="font-display text-4xl text-cream">
            {t(cmsErr ? "This story isn't published yet" : (error ? "Could not generate this article" : "Article not found"))}
          </h1>
          {cmsErr && (
            <p className="mt-4 font-body text-ash">{t("It may be awaiting review. Browse other published community stories in the Library.")}</p>
          )}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            {error && isDynamic && !isCms && (
              <button onClick={() => setRetryKey((k) => k + 1)} data-testid="article-retry"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-body font-medium text-ink transition-transform hover:scale-105">
                <Sparkles className="h-4 w-4" /> {t("Try again")}
              </button>
            )}
            {cmsErr && (
              <Link to="/library" data-testid="cms-error-library"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-body font-medium text-ink transition-transform hover:scale-105">
                <BookOpen className="h-4 w-4" /> {t("Open the Community Library")}
              </Link>
            )}
            <Link to="/" className="inline-block font-body text-gold hover:underline">{t("Back to home")}</Link>
          </div>
        </div>
      </div>
    );
  }

  const isHistory = a.group === "history";
  const series = SERIES[a.group];
  const isSeries = !!series;
  const idx = isSeries ? series.findIndex((s) => s.slug === slug) : -1;
  const prev = idx > 0 ? series[idx - 1] : null;
  const next = idx >= 0 && idx < series.length - 1 ? series[idx + 1] : null;
  const seriesLabel = (s) => `${s.year ? s.year + " · " : ""}${s.tab}`;
  const wiki = a.wiki || WIKI[slug];

  return (
    <div className="min-h-screen bg-paper" data-testid="article-page">
      <InnerNav />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-edge">
        {a.hero && (
          <div className="absolute inset-0">
            <img src={a.hero} alt="" className="h-full w-full object-cover opacity-25 grayscale" />
            <div className="absolute inset-0 bg-gradient-to-b from-paper/60 via-paper/80 to-paper" />
          </div>
        )}
        <div className="relative mx-auto max-w-3xl px-6 py-16 md:py-20">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <div className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.28em] text-gold">
              <BookOpen className="h-4 w-4" /> {t(a.category)}
            </div>
            <h1 data-testid="article-title" className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
              {t(a.title)}
            </h1>
            {a.subtitle && (
              <p className="mt-5 font-body text-lg leading-relaxed text-ash md:text-xl">{t(a.subtitle)}</p>
            )}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <ListenButton text={articlePlainText(a)} testId="article-listen" label="Listen to this article" sourceId={`article-${slug}`} sourceLabel={a.title} />
              {a.community ? (
                <span data-testid="community-badge" className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 font-body text-xs text-gold">
                  <BookOpen className="h-3.5 w-3.5" /> {t("Community contribution")}{a.author ? ` · ${a.author}` : ""}
                </span>
              ) : isDynamic && (
                <span data-testid="ai-badge" className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 font-body text-xs text-gold">
                  <Sparkles className="h-3.5 w-3.5" /> {t("AI-generated overview")}
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Series tab bar (History / Music) */}
      {isSeries && (
        <div className="border-b border-edge bg-paper/90" data-testid="series-tabs">
          <div className="mx-auto flex max-w-3xl gap-2 overflow-x-auto px-6 py-3">
            {series.map((s) => {
              const active = s.slug === slug;
              return (
                <Link key={s.slug} to={`/article/${s.slug}`} data-testid={`series-tab-${s.slug}`}
                  className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 font-body text-sm transition-colors ${
                    active ? "border-gold bg-gold/10 text-gold" : "border-edge text-ash hover:border-gold hover:text-gold"
                  }`}>
                  {s.year && <span className="font-display text-base font-semibold">{s.year}</span>}
                  <span className={s.year ? "hidden sm:inline" : ""}>{t(s.tab)}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Body */}
      <article className="mx-auto max-w-3xl px-6 py-14 md:py-16">
        {isHistory && slug !== "history-overview" && (
          <motion.p initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
            className="mb-12 border-l-2 border-gold pl-5 font-body text-lg italic leading-relaxed text-ash"
            data-testid="history-intro">
            {t(HISTORY_INTRO)}
          </motion.p>
        )}

        <div className="space-y-6">
          {a.blocks.map((b, i) => {
            if (b.t === "h2") {
              return (
                <motion.h2 key={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={fade}
                  className="!mt-14 font-display text-3xl font-semibold tracking-tight text-cream md:text-4xl">
                  {t(b.x)}
                </motion.h2>
              );
            }
            if (b.t === "caption") {
              return (
                <motion.p key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                  className="font-body text-base italic text-gold">{t(b.x)}</motion.p>
              );
            }
            if (b.t === "ul") {
              return (
                <motion.ul key={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} variants={fade}
                  className="!mt-4 space-y-3 pl-1">
                  {(b.items || []).map((it, j) => (
                    <li key={j} className="flex gap-3 font-body text-lg leading-relaxed text-cream md:text-xl">
                      <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                      <span>{t(it)}</span>
                    </li>
                  ))}
                </motion.ul>
              );
            }
            if (b.t === "embed") {
              if (b.name === "landmark-serials") return <LandmarkSerials key={i} />;
              return null;
            }
            if (b.t === "table") {
              return (
                <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} variants={fade}
                  className="!mt-6 overflow-x-auto rounded-2xl border border-edge">
                  <table className="w-full min-w-[640px] border-collapse text-left">
                    <thead>
                      <tr className="bg-surface/60">
                        {(b.head || []).map((h, j) => (
                          <th key={j} className="border-b border-edge px-5 py-3 font-body text-xs uppercase tracking-[0.18em] text-gold">
                            {t(h)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(b.rows || []).map((row, ri) => (
                        <tr key={ri} className="align-top odd:bg-surface/20">
                          {row.map((cell, ci) => (
                            <td key={ci} className={`border-b border-edge px-5 py-4 font-body text-base leading-relaxed ${ci === 0 ? "font-display text-lg text-cream" : "text-ash"}`}>
                              {t(cell)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </motion.div>
              );
            }
            const lead = b.t === "lead";
            return (
              <motion.p key={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} variants={fade}
                className={`font-body leading-relaxed text-cream ${lead ? "text-xl md:text-2xl md:leading-relaxed" : "text-lg md:text-xl md:leading-loose"}`}>
                {t(b.x)}
              </motion.p>
            );
          })}
        </div>

        {/* Series prev / next */}
        {isSeries && (
          <div className="mt-16 flex items-center justify-between gap-4 border-t border-edge pt-8" data-testid="series-prevnext">
            {prev ? (
              <Link to={`/article/${prev.slug}`} data-testid="series-prev"
                className="group flex items-center gap-3 rounded-2xl border border-edge bg-surface/40 px-5 py-4 transition-colors hover:border-gold">
                <ChevronLeft className="h-5 w-5 text-gold" />
                <span className="text-left">
                  <span className="block font-body text-xs uppercase tracking-widest text-ash">{t("Previous")}</span>
                  <span className="block font-display text-lg text-cream">{seriesLabel(prev)}</span>
                </span>
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/article/${next.slug}`} data-testid="series-next"
                className="group flex items-center gap-3 rounded-2xl border border-edge bg-surface/40 px-5 py-4 text-right transition-colors hover:border-gold">
                <span>
                  <span className="block font-body text-xs uppercase tracking-widest text-ash">{t("Next")}</span>
                  <span className="block font-display text-lg text-cream">{seriesLabel(next)}</span>
                </span>
                <ChevronRight className="h-5 w-5 text-gold" />
              </Link>
            ) : <span />}
          </div>
        )}

        {/* Wikipedia reference */}
        {wiki && (
          <a href={wiki.url} target="_blank" rel="noopener noreferrer" data-testid="wiki-link"
            className="group mt-14 block rounded-2xl border border-edge bg-surface/40 p-6 transition-colors hover:border-gold">
            <div className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.25em] text-ash">
              <BookOpen className="h-4 w-4" /> {t("From Wikipedia")}
            </div>
            <div className="mt-2 font-display text-xl text-cream">{wiki.title}</div>
            <p className="mt-2 font-body text-base leading-relaxed text-ash">{t(wiki.excerpt)}</p>
            <span className="mt-3 inline-flex items-center gap-1.5 font-body text-sm text-gold group-hover:underline">
              {t("Read more on Wikipedia")} <ExternalLink className="h-3.5 w-3.5" />
            </span>
          </a>
        )}

        {/* CTA */}
        <div className="mt-16 flex flex-wrap items-center gap-4 rounded-3xl border border-edge bg-surface/40 p-8">
          <Film className="h-8 w-8 text-gold" />
          <div className="flex-1">
            <div className="font-display text-2xl text-cream">{t(a.cta ? a.cta.heading || "Keep exploring" : "Explore the catalogue")}</div>
            <div className="font-body text-sm text-ash">{t(a.cta ? a.cta.sub || "Dive deeper into Indian cinema, television and OTT." : "Discover films across every Indian language, era and mood.")}</div>
          </div>
          <Link to={a.cta ? a.cta.to : "/movies"} data-testid="article-browse-cta"
            className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-body font-medium text-ink transition-transform hover:scale-105">
            {t(a.cta ? a.cta.label : "Browse Movies")} <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </article>

      <div className="border-t border-edge">
        <Discussion entityType="article" entityId={slug} title="Join the discussion" />
      </div>
    </div>
  );
}
